import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';

interface PageTitleProps {
  title?: string;
}

type Meta = {
  title: string;
  description: string;
  robots: string;
  canonicalPath: string;
  ogType?: 'website' | 'article';
};

const SITE_URL = 'https://sentihype.com';
const DEFAULT_TITLE = 'SentiHype - Real-time Social Sentiment Analytics for Stocks & Crypto';
const DEFAULT_DESCRIPTION = 'Track Reddit and social sentiment for stocks and crypto. Discover trending tickers, spike detection, and price linkage analytics.';

const isPrivatePath = (path: string): boolean => {
  return (
    path === '/login' ||
    path.startsWith('/auth/') ||
    path.startsWith('/dashboard') ||
    path.startsWith('/billing') ||
    path.startsWith('/profile')
  );
};

const getMetaByPath = (path: string): Meta => {
  if (path.startsWith('/ticker/')) {
    const ticker = path.split('/')[2]?.toUpperCase() || 'Ticker';
    return {
      title: `${ticker} Stock Sentiment Analysis | SentiHype`,
      description: `Analyze ${ticker} sentiment from Reddit discussions: mentions trend, sentiment distribution, WSS, spikes, and statistical price linkage.`,
      robots: 'index,follow',
      canonicalPath: `/ticker/${ticker}`,
      ogType: 'website',
    };
  }

  if (path === '/') {
    return {
      title: DEFAULT_TITLE,
      description: DEFAULT_DESCRIPTION,
      robots: 'index,follow',
      canonicalPath: '/',
      ogType: 'website',
    };
  }

  if (path === '/market') {
    return {
      title: 'Market Sentiment Dashboard | SentiHype',
      description: 'See top trending tickers, sentiment shifts, and social activity snapshots across the market in one dashboard.',
      robots: 'index,follow',
      canonicalPath: '/market',
      ogType: 'website',
    };
  }

  if (path === '/about') {
    return {
      title: 'About SentiHype | Social Sentiment Analytics',
      description: 'Learn how SentiHype turns Reddit and social chatter into structured market sentiment analytics for traders and investors.',
      robots: 'index,follow',
      canonicalPath: '/about',
      ogType: 'website',
    };
  }

  if (path === '/pricing') {
    return {
      title: 'Pricing - Free vs Premium Plans | SentiHype',
      description: 'Compare SentiHype Free and Premium: real-time data, extended history, spike detection, and Price Linkage analytics.',
      robots: 'index,follow',
      canonicalPath: '/pricing',
      ogType: 'website',
    };
  }

  if (path === '/blog') {
    return {
      title: 'Blog | SentiHype',
      description: 'Case studies and product updates on social sentiment analytics for stocks and crypto.',
      robots: 'index,follow',
      canonicalPath: '/blog',
      ogType: 'website',
    };
  }

  if (path === '/blog/opendoor') {
    return {
      title: "How SentiHype Detected Opendoor's Meme Stock Surge | SentiHype Blog",
      description: 'Case study: how SentiHype detected the OPEN social spike before the major meme-stock move.',
      robots: 'index,follow',
      canonicalPath: '/blog/opendoor',
      ogType: 'article',
    };
  }

  if (path === '/terms') {
    return {
      title: 'Terms of Service | SentiHype',
      description: 'Read the SentiHype terms of service.',
      robots: 'index,follow',
      canonicalPath: '/terms',
      ogType: 'website',
    };
  }

  if (path === '/privacy') {
    return {
      title: 'Privacy Policy | SentiHype',
      description: 'Read the SentiHype privacy policy and data handling information.',
      robots: 'index,follow',
      canonicalPath: '/privacy',
      ogType: 'website',
    };
  }

  if (path === '/risk-disclosure') {
    return {
      title: 'Risk Disclosure | SentiHype',
      description: 'Important risk disclosure for using social sentiment analytics in trading and investing decisions.',
      robots: 'index,follow',
      canonicalPath: '/risk-disclosure',
      ogType: 'website',
    };
  }

  if (path === '/search') {
    return {
      title: 'Content Search | SentiHype',
      description: 'Search social sentiment content and discussions with advanced filters.',
      robots: 'index,follow',
      canonicalPath: '/search',
      ogType: 'website',
    };
  }

  return {
    title: 'SentiHype | Social Sentiment Analytics',
    description: DEFAULT_DESCRIPTION,
    robots: isPrivatePath(path) ? 'noindex,nofollow' : 'index,follow',
    canonicalPath: path || '/',
    ogType: 'website',
  };
};

const normalizeTitle = (pageTitle: string): string => {
  if (pageTitle.includes('SentiHype')) return pageTitle;
  return `${pageTitle} | SentiHype`;
};

const PageTitle: React.FC<PageTitleProps> = ({ title }) => {
  const location = useLocation();
  const path = location.pathname || '/';
  const routeMeta = getMetaByPath(path);

  const finalTitle = title ? normalizeTitle(title) : routeMeta.title;
  const finalDescription = routeMeta.description;
  const finalRobots = isPrivatePath(path) ? 'noindex,nofollow' : routeMeta.robots;
  const finalCanonical = `${SITE_URL}${routeMeta.canonicalPath}`;
  const finalOgType = routeMeta.ogType || 'website';

  return (
    <Helmet>
      <title>{finalTitle}</title>
      <meta name="description" content={finalDescription} />
      <meta name="robots" content={finalRobots} />
      <link rel="canonical" href={finalCanonical} />

      <meta property="og:type" content={finalOgType} />
      <meta property="og:title" content={finalTitle} />
      <meta property="og:description" content={finalDescription} />
      <meta property="og:url" content={finalCanonical} />
      <meta property="og:site_name" content="SentiHype" />
      <meta property="og:image" content={`${SITE_URL}/logo512.png`} />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={finalTitle} />
      <meta name="twitter:description" content={finalDescription} />
      <meta name="twitter:image" content={`${SITE_URL}/logo512.png`} />
    </Helmet>
  );
};

export default PageTitle;
